import React from 'react';
import { Box, Button, Container, Grid, Typography, Card, CardContent, makeStyles, Theme } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import VideocamIcon from '@material-ui/icons/Videocam';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import SettingsIcon from '@material-ui/icons/Settings';
import PeopleIcon from '@material-ui/icons/People';
import BuildIcon from '@material-ui/icons/Build';
import SpeedIcon from '@material-ui/icons/Speed';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    padding: theme.spacing(2, 0),
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  },
  nav: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logo: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: 'white',
    textDecoration: 'none',
  },
  authButtons: {
    '& > *': {
      marginLeft: theme.spacing(1),
    },
  },
  loginButton: {
    borderColor: 'rgba(255, 255, 255, 0.8)',
    color: 'rgba(255, 255, 255, 0.9)',
    '&:hover': {
      borderColor: 'white',
      color: 'white',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  signupButton: {
    backgroundColor: '#34495e',
    color: 'white',
    '&:hover': {
      backgroundColor: '#2c3e50',
    },
  },
  hero: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    color: 'white',
    padding: theme.spacing(8, 0),
  },
  heroContent: {
    maxWidth: '800px',
    margin: '0 auto',
  },
  heroTitle: {
    fontSize: '3.5rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
      fontSize: '2.5rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
    },
  },
  heroSubtitle: {
    fontSize: '1.4rem',
    marginBottom: theme.spacing(4),
    opacity: 0.9,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.2rem',
    },
  },
  ctaButton: {
    backgroundColor: '#2980b9',
    color: 'white',
    padding: theme.spacing(1.5, 4),
    fontSize: '1.2rem',
    fontWeight: 'bold',
    '&:hover': {
      backgroundColor: '#3498db',
    },
  },
  featuresSection: {
    backgroundColor: '#f8f9fa',
    padding: theme.spacing(8, 0),
  },
  sectionTitle: {
    textAlign: 'center',
    marginBottom: theme.spacing(6),
    color: '#2c3e50',
    fontWeight: 'bold',
  },
  featureCard: {
    height: '100%',
    textAlign: 'center',
    padding: theme.spacing(3),
    border: '1px solid #e9ecef',
    borderRadius: theme.spacing(1),
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    backgroundColor: 'white',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 4px 20px rgba(44, 62, 80, 0.1)',
    },
  },
  featureIcon: {
    fontSize: '3rem',
    color: '#3498db',
    marginBottom: theme.spacing(2),
  },
  featureTitle: {
    marginBottom: theme.spacing(2),
    color: '#2c3e50',
    fontWeight: 'bold',
  },
  featureDescription: {
    color: '#6c757d',
    lineHeight: 1.6,
  },
  footer: {
    backgroundColor: '#2c3e50',
    color: 'white',
    padding: theme.spacing(4, 0),
    textAlign: 'center',
  },
}));

const LandingPage: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();

  const features = [
    {
      icon: <VideocamIcon className={classes.featureIcon} />,
      title: 'Live Video Streaming',
      description:
        'Junior technicians can livestream their view to experienced senior technicians in real-time, enabling remote guidance and support.',
    },
    {
      icon: <SettingsIcon className={classes.featureIcon} />,
      title: 'AI-Powered Analysis',
      description:
        'Advanced AI analyzes photos and videos to identify HVAC issues, error codes, and provide instant diagnostic insights.',
    },
    {
      icon: <PeopleIcon className={classes.featureIcon} />,
      title: 'Remote Mentoring',
      description:
        'Senior technicians can be the "eyes and brains" while junior technicians serve as the "hands" in the field.',
    },
    {
      icon: <CameraAltIcon className={classes.featureIcon} />,
      title: 'Visual Documentation',
      description:
        'Capture and analyze images of HVAC units, refrigeration systems, and equipment for comprehensive diagnostics.',
    },
    {
      icon: <BuildIcon className={classes.featureIcon} />,
      title: 'Expert Guidance',
      description:
        'Get instant advice on repairs, maintenance procedures, and troubleshooting from experienced professionals.',
    },
    {
      icon: <SpeedIcon className={classes.featureIcon} />,
      title: 'Faster Resolution',
      description:
        'Reduce service time and improve first-time fix rates with real-time collaboration and AI assistance.',
    },
  ];

  const handleLogin = () => {
    history.push('/login');
  };

  const handleSignup = () => {
    history.push('/signup');
  };

  const handleGetStarted = () => {
    history.push('/signup');
  };

  return (
    <div className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Container maxWidth="lg">
          <Box className={classes.nav}>
            <Typography variant="h4" className={classes.logo}>
              BLUESTREAM
            </Typography>
            <Box className={classes.authButtons}>
              <Button variant="outlined" color="inherit" onClick={handleLogin} className={classes.loginButton}>
                Login
              </Button>
              <Button variant="contained" color="secondary" onClick={handleSignup} className={classes.signupButton}>
                Sign Up
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box className={classes.hero}>
        <Container maxWidth="lg">
          <Box className={classes.heroContent}>
            <Typography variant="h1" className={classes.heroTitle}>
              Revolutionize HVAC Field Service
            </Typography>
            <Typography variant="h5" className={classes.heroSubtitle}>
              Connect junior technicians in the field with senior experts remotely. Use AI-powered diagnostics to solve
              problems faster than ever before.
            </Typography>
            <Button variant="contained" size="large" className={classes.ctaButton} onClick={handleGetStarted}>
              Get Started Today
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Features Section */}
      <Box className={classes.featuresSection}>
        <Container maxWidth="lg">
          <Typography variant="h2" className={classes.sectionTitle}>
            Powerful Features for Modern HVAC Teams
          </Typography>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Card className={classes.featureCard}>
                  <CardContent>
                    {feature.icon}
                    <Typography variant="h5" className={classes.featureTitle}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" className={classes.featureDescription}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box className={classes.footer}>
        <Container maxWidth="lg">
          <Typography variant="body2">
            © {new Date().getFullYear()} Bluestream. All rights reserved. Empowering HVAC professionals worldwide.
          </Typography>
        </Container>
      </Box>
    </div>
  );
};

export default LandingPage;
